import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { fetchSummaryByAppointment } from "../../api/summary";
import { useDispatch, useSelector } from "react-redux";
import { fetchAppointmentDetails } from "../../redux/appointment-actions";
import { fetchSoapNotes } from "../../api/soap";
import { fetchSummaryofSummaries } from "../../api/summaryOfSummaries";
import LoadingCard from "./LoadingCard";
import SummaryOfPatient from "../patients/SummaryOfPatient";
import { fetchCallHistory } from "../../api/callHistory";

const SUMMARY_CYCLE_MS = 60 * 1000;
const SUMMARY_POLL_INTERVAL_MS = 3000;
const POST_CALL_WINDOW_MS = 10 * 60 * 1000;

const extractReasonFromSoap = (soapText) => {
  const match = String(soapText || "").match(
    /Reason for Visit\s*[-–—:]?\s*(.*?)(\n|$)/i
  );
  return match ? match[1].trim() : null;
};

const getSummaryText = (summaryResponse) =>
  summaryResponse?.data?.data?.full_summary_text ||
  summaryResponse?.data?.full_summary_text ||
  summaryResponse?.full_summary_text ||
  summaryResponse?.summary ||
  "";

const getSoapNotesText = (soapResponse) =>
  soapResponse?.data?.data?.data?.soap_notes ??
  soapResponse?.data?.data?.soap_notes ??
  soapResponse?.data?.soap_notes ??
  soapResponse?.soap_notes ??
  "";

const getCallHistoryEntryForAppointment = (historyList = [], appointmentId) =>
  historyList.find(
    (entry) => String(entry?.appointmentID) === String(appointmentId)
  );

const Summary = ({
  appointmentId,
  username,
  patientId,
  callEndTime,
  postCallWindowExpired = false,
  summaryFinalFetchStatus = "idle",
}) => {
  const [selectedAppointment, setSelectedAppointment] = useState({});
  const dispatch = useDispatch();
  const appointments = useSelector(
    (state) => state.appointments?.appointments || []
  );

  const [cycleStartedAt, setCycleStartedAt] = useState(() => Date.now());
  const [cycleVersion, setCycleVersion] = useState(0);
  const [summaryPollingFinished, setSummaryPollingFinished] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);
  const identityRef = useRef(`${appointmentId || ""}:${username || ""}`);

  const callEndTimestamp = callEndTime ? new Date(callEndTime).getTime() : NaN;
  const calculatedDeadlineExpired =
    Number.isFinite(callEndTimestamp) &&
    Date.now() >= callEndTimestamp + POST_CALL_WINDOW_MS;
  const deadlineExpired =
    postCallWindowExpired || calculatedDeadlineExpired;

  const callHistoryQuery = useQuery({
    queryKey: ["call-history-summary", username, appointmentId],
    queryFn: () => fetchCallHistory([username]),
    enabled: Boolean(username),
    refetchInterval: (query) => {
      const matchingEntry = getCallHistoryEntryForAppointment(
        query.state.data || [],
        appointmentId
      );
      return matchingEntry?.endTime ? false : 3000;
    },
    refetchIntervalInBackground: true,
  });

  const { data: callHistoryList = [] } = callHistoryQuery;
  const callHistoryEntry = getCallHistoryEntryForAppointment(
    callHistoryList,
    appointmentId
  );

  const summaryQuery = useQuery({
    queryKey: ["summary", appointmentId, username],
    queryFn: ({ signal }) =>
      fetchSummaryByAppointment(
        `${username}_${appointmentId}_summary`,
        username,
        signal
      ),
    // We explicitly start and stop each 60-second cycle below. This lets the
    // Retry button start a fresh cycle even when React Query has cached an empty response.
    enabled: false,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: summaryData, refetch: refetchSummary } = summaryQuery;
  const summary = useMemo(() => getSummaryText(summaryData), [summaryData]);

  const { data: soapData } = useQuery({
    queryKey: ["soap-notes", appointmentId, username],
    queryFn: async ({ signal }) => {
      const response = await fetchSoapNotes(
        `${username}_${appointmentId}_soap`,
        username,
        signal
      );
      if (!String(getSoapNotesText(response) || "").trim()) {
        throw new Error("SOAP notes are not available yet.");
      }
      return response;
    },
    enabled: Boolean(username && appointmentId && !deadlineExpired),
    retry: 3,
    staleTime: Infinity,
    gcTime: Infinity,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const { data: longitudinalData, isLoading: loadingLongitudinal } = useQuery({
    queryKey: ["longitudinal-summary", patientId],
    queryFn: () => fetchSummaryofSummaries(patientId),
    enabled: Boolean(patientId),
  });

  useEffect(() => {
    if (!appointments.length && username) {
      dispatch(fetchAppointmentDetails(username));
    }
  }, [dispatch, username, appointments]);

  useEffect(() => {
    setSelectedAppointment(
      appointments.find(
        (appointment) => String(appointment.id) === String(appointmentId)
      ) || {}
    );
  }, [appointmentId, appointments]);

  // A remount normally occurs when the doctor switches away and returns to Summary.
  // Reset the 60-second cycle if the call identity changes without a remount.
  useEffect(() => {
    const nextIdentity = `${appointmentId || ""}:${username || ""}`;
    if (identityRef.current === nextIdentity) return;

    identityRef.current = nextIdentity;
    setCycleStartedAt(Date.now());
    setCycleVersion((version) => version + 1);
    setSummaryPollingFinished(false);
    setSecondsRemaining(60);
  }, [appointmentId, username]);

  const startNewSummaryCycle = useCallback(() => {
    if (deadlineExpired) return;
    setCycleStartedAt(Date.now());
    setCycleVersion((version) => version + 1);
    setSummaryPollingFinished(false);
    setSecondsRemaining(60);
  }, [deadlineExpired]);

  useEffect(() => {
    if (
      !username ||
      !appointmentId ||
      deadlineExpired ||
      summary ||
      summaryPollingFinished
    ) {
      return undefined;
    }

    let active = true;
    let requestInFlight = false;
    const startedAt = cycleStartedAt;
    let nextFetchAt = startedAt;

    const fetchOnce = async () => {
      if (!active || requestInFlight || Date.now() >= startedAt + SUMMARY_CYCLE_MS) {
        return;
      }

      requestInFlight = true;
      try {
        await refetchSummary();
      } finally {
        requestInFlight = false;
      }
    };

    // Fetch immediately when this cycle begins, then continue every 3 seconds.
    void fetchOnce();
    nextFetchAt = startedAt + SUMMARY_POLL_INTERVAL_MS;

    const intervalId = window.setInterval(() => {
      if (!active) return;

      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(
        Math.ceil((SUMMARY_CYCLE_MS - elapsed) / 1000),
        0
      );
      setSecondsRemaining(remaining);

      if (elapsed >= SUMMARY_CYCLE_MS) {
        setSummaryPollingFinished(true);
        setSecondsRemaining(0);
        window.clearInterval(intervalId);
        return;
      }

      if (Date.now() >= nextFetchAt) {
        nextFetchAt = Date.now() + SUMMARY_POLL_INTERVAL_MS;
        void fetchOnce();
      }
    }, 1000);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [
    appointmentId,
    username,
    deadlineExpired,
    summary,
    summaryPollingFinished,
    cycleStartedAt,
    cycleVersion,
    refetchSummary,
  ]);

  const reasonForVisit =
    selectedAppointment?.reason ||
    extractReasonFromSoap(getSoapNotesText(soapData)) ||
    "Not specified";

  const finalFetchPending =
    deadlineExpired && !summary && summaryFinalFetchStatus !== "done";
  const isSummaryPending =
    !deadlineExpired && !summary && !summaryPollingFinished;

  if (finalFetchPending) {
    return <LoadingCard message="Checking for the final summary…" />;
  }

  if (isSummaryPending) {
    return (
      <LoadingCard
        message={`Summary's stitching up… hang tight. ${secondsRemaining}s`}
      />
    );
  }

  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h3 className="font-medium text-blue-700 mb-2 flex items-center">
          <FileText className="w-5 h-5 mr-2" /> Call Summary
        </h3>

        <div className="text-sm text-neutral-700">
          <p className="mb-2">
            <span className="font-bold">Patient:</span>{" "}
            {selectedAppointment?.full_name || callHistoryEntry?.patientName || "—"}
          </p>
          <p className="mb-2">
            <span className="font-bold">Start Time:</span>{" "}
            {callHistoryEntry?.startTime
              ? new Date(callHistoryEntry.startTime).toLocaleString()
              : "—"}
          </p>
          <p className="mb-2">
            <span className="font-bold">End Time:</span>{" "}
            {callHistoryEntry?.endTime
              ? new Date(callHistoryEntry.endTime).toLocaleString()
              : callEndTime
                ? new Date(callEndTime).toLocaleString()
                : "—"}
          </p>
          <p className="mb-2">
            <span className="font-bold">Reason for Visit:</span>{" "}
            {reasonForVisit}
          </p>

          <div className="mb-2">
            <p className="font-bold">Summary from AI:</p>
            {summary ? (
              <p className="whitespace-pre-wrap">{summary}</p>
            ) : deadlineExpired ? (
              <p>No summary data available.</p>
            ) : (
              <div>
                <p>Summary data isn't available yet. Please try again.</p>
                {summaryPollingFinished && (
                  <button
                    type="button"
                    onClick={startNewSummaryCycle}
                    disabled={!summaryPollingFinished}
                    className="mt-3 inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Retry Summary (60 seconds)
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
        <h3 className="font-medium text-blue-700 mb-2 flex items-center">
          <FileText className="w-5 h-5 mr-2" /> Longitudinal Summary
        </h3>

        {loadingLongitudinal ? (
          <LoadingCard message="Fetching longitudinal summary…" />
        ) : longitudinalData && longitudinalData.overall_summary ? (
          <SummaryOfPatient summaryDataProp={longitudinalData} />
        ) : (
          <div className="text-sm text-neutral-600">
            No longitudinal summary available for this patient.
          </div>
        )}
      </div>
    </>
  );
};

export default Summary;
